import { z } from "zod";

/**
 * Dashboard capability registry — the single vocabulary for active and
 * planned non-owner dashboard access. `GRANTABLE_DASHBOARD_SECTIONS` is the
 * live allowlist; planned declarations remain inert until their routes and
 * guards activate atomically.
 *
 * Before this file the same list existed three times and agreed by luck:
 * the nav keys (`apps/dashboard/src/lib/navigation/dashboard.tsx`), the
 * hand-placed page guards (`requireSectionAccess.ts` call sites), and the
 * API's inline authorization checks. A section added to the nav was silently
 * owner-only; a route added without a guard was silently open. All three now
 * import from here.
 *
 * See plans/manager-section-permissions-spec.md.
 */

/**
 * Sections an owner can grant to a MANAGER-role team member.
 *
 * Ordered by landing priority — operations, then money, content, settings —
 * NOT by nav position. `firstGrantedSection` walks this order to pick where a
 * manager lands, so the most operational section they hold wins.
 */
export const GRANTABLE_DASHBOARD_SECTIONS = [
  // Operations
  "appointments",
  "requests",
  "report",
  "campaigns",
  // Task manager add-on (Oct 2026): opening/closing duty review.
  "tasks",
  "clients",
  "chat",
  "cancellations",
  "notifications",
  // Product commerce (activated Task 14, Aug 2026 — routes, nav, guards and
  // grants shipped atomically). Landing priority: operational retail pages
  // sit after the service ops suite and before money.
  "products",
  "product-inventory",
  "product-sales",
  "product-reports",
  // Money (read + refund issuance; payout *destination* stays owner-only)
  "payouts",
  "transactions",
  "deposits",
  "refunds",
  "offers",
  // Non-navigable cost modifier: grantable, but never a landing page or nav
  // entry — it refines fields inside Products/Inventory/Reports.
  "product-costs",
  // Content
  "photos",
  "feedback",
  "marketing",
  "add-ons",
  "logs",
  // Settings subtrees — deliberately split; see OWNER_ONLY_DASHBOARD_SECTIONS
  "settings-business",
  "settings-services",
  "settings-schedules",
  "settings-booking",
] as const;

export type DashboardSection = (typeof GRANTABLE_DASHBOARD_SECTIONS)[number];

/**
 * Product-commerce capabilities — ACTIVE since Task 14 (Aug 2026), when their
 * guarded routes, grouped navigation, manager controls, and landing behavior
 * shipped atomically. Kept as a named subset for the permissions modal's
 * grouping and the drift tests that pin the activation.
 */
export const PRODUCT_COMMERCE_DASHBOARD_CAPABILITIES = [
  "products",
  "product-inventory",
  "product-sales",
  "product-reports",
  "product-costs",
] as const;

export type ProductCommerceDashboardCapability =
  (typeof PRODUCT_COMMERCE_DASHBOARD_CAPABILITIES)[number];

/**
 * Complete dashboard capability vocabulary. Since Task 14 every capability is
 * grantable, so this is the grantable tuple itself; the alias survives for
 * consumers (and future planned capabilities) that distinguish "any known
 * capability" from "grantable today".
 */
export const DASHBOARD_CAPABILITIES = [
  ...GRANTABLE_DASHBOARD_SECTIONS,
] as const;

export type DashboardCapability = (typeof DASHBOARD_CAPABILITIES)[number];

export const dashboardSectionSchema = z.enum(GRANTABLE_DASHBOARD_SECTIONS);
export const dashboardCapabilitySchema = z.enum(DASHBOARD_CAPABILITIES);

/**
 * Surfaces that are owner-only forever. They are deliberately **not** members
 * of `DashboardSection`, so no hand-crafted API call can grant them — you
 * cannot grant a key the enum does not contain.
 *
 * - `settings-team` edits `role` and `managerSections`: a manager holding it
 *   could grant themselves every other section (self-escalation).
 * - `settings-payment` changes the Stripe payout destination.
 * - `settings-tracking` controls third-party measurement destinations and
 *   visitor-consent behavior.
 * - `billing` is account-level, not business-scoped.
 *
 * Listed here (rather than omitted entirely) so the drift test can assert
 * every dashboard section is classified exactly once.
 *
 * Never grantable to a manager. A business admin (Oct 2026,
 * `ADMIN_OPEN_OWNER_ONLY_SECTIONS` in ./businessAdmin) may open
 * `settings-team` and `settings-tracking`; payment and billing stay the
 * owner's alone.
 */
export const OWNER_ONLY_DASHBOARD_SECTIONS = [
  "settings-team",
  "settings-payment",
  "settings-tracking",
  "billing",
] as const;

export type OwnerOnlyDashboardSection =
  (typeof OWNER_ONLY_DASHBOARD_SECTIONS)[number];

export type DashboardSectionGroup =
  | "operations"
  | "money"
  | "content"
  | "settings";

export type DashboardCapabilityAvailability = "active" | "planned";

export type DashboardCapabilityMeta = {
  group: DashboardSectionGroup;
  labelKey: string;
  navigable: boolean;
  availability: DashboardCapabilityAvailability;
};

/**
 * Exhaustive metadata for active and planned dashboard capabilities.
 * `labelKey` resolves under the
 * `settings.members.permissions.sections` i18n namespace. Live consumers still
 * iterate `GRANTABLE_DASHBOARD_SECTIONS`, so planned entries remain inert.
 *
 * The grouping here is the permissions modal's coarse presentation grouping,
 * not the recursive Products/Sales/Reports navigation hierarchy owned by Task
 * 14.
 */
export const DASHBOARD_SECTION_META = {
  appointments: {
    group: "operations",
    labelKey: "appointments",
    navigable: true,
    availability: "active",
  },
  requests: {
    group: "operations",
    labelKey: "requests",
    navigable: true,
    availability: "active",
  },
  report: {
    group: "operations",
    labelKey: "report",
    navigable: true,
    availability: "active",
  },
  campaigns: {
    group: "operations",
    labelKey: "campaigns",
    navigable: true,
    availability: "active",
  },
  tasks: {
    group: "operations",
    labelKey: "tasks",
    navigable: true,
    availability: "active",
  },
  clients: {
    group: "operations",
    labelKey: "clients",
    navigable: true,
    availability: "active",
  },
  chat: {
    group: "operations",
    labelKey: "chat",
    navigable: true,
    availability: "active",
  },
  cancellations: {
    group: "operations",
    labelKey: "cancellations",
    navigable: true,
    availability: "active",
  },
  notifications: {
    group: "operations",
    labelKey: "notifications",
    navigable: true,
    availability: "active",
  },
  payouts: {
    group: "money",
    labelKey: "payouts",
    navigable: true,
    availability: "active",
  },
  transactions: {
    group: "money",
    labelKey: "transactions",
    navigable: true,
    availability: "active",
  },
  deposits: {
    group: "money",
    labelKey: "deposits",
    navigable: true,
    availability: "active",
  },
  refunds: {
    group: "money",
    labelKey: "refunds",
    navigable: true,
    availability: "active",
  },
  offers: {
    group: "money",
    labelKey: "offers",
    navigable: true,
    availability: "active",
  },
  photos: {
    group: "content",
    labelKey: "photos",
    navigable: true,
    availability: "active",
  },
  feedback: {
    group: "content",
    labelKey: "feedback",
    navigable: true,
    availability: "active",
  },
  marketing: {
    group: "content",
    labelKey: "marketing",
    navigable: true,
    availability: "active",
  },
  "add-ons": {
    group: "content",
    labelKey: "addOns",
    navigable: true,
    availability: "active",
  },
  logs: {
    group: "content",
    labelKey: "logs",
    navigable: true,
    availability: "active",
  },
  "settings-business": {
    group: "settings",
    labelKey: "settingsBusiness",
    navigable: true,
    availability: "active",
  },
  "settings-services": {
    group: "settings",
    labelKey: "settingsServices",
    navigable: true,
    availability: "active",
  },
  "settings-schedules": {
    group: "settings",
    labelKey: "settingsSchedules",
    navigable: true,
    availability: "active",
  },
  "settings-booking": {
    group: "settings",
    labelKey: "settingsBooking",
    navigable: true,
    availability: "active",
  },
  products: {
    group: "operations",
    labelKey: "products",
    navigable: true,
    availability: "active",
  },
  "product-inventory": {
    group: "operations",
    labelKey: "productInventory",
    navigable: true,
    availability: "active",
  },
  "product-sales": {
    group: "operations",
    labelKey: "productSales",
    navigable: true,
    availability: "active",
  },
  "product-reports": {
    group: "operations",
    labelKey: "productReports",
    navigable: true,
    availability: "active",
  },
  "product-costs": {
    group: "money",
    labelKey: "productCosts",
    navigable: false,
    availability: "active",
  },
} as const satisfies Record<DashboardCapability, DashboardCapabilityMeta>;

export type NonNavigableDashboardCapability = {
  [Capability in DashboardCapability]: (typeof DASHBOARD_SECTION_META)[Capability]["navigable"] extends false
    ? Capability
    : never;
}[DashboardCapability];

export type NavigableDashboardCapability = Exclude<
  DashboardCapability,
  NonNavigableDashboardCapability
>;

export type ProductCommerceNavigableDashboardCapability = Extract<
  NavigableDashboardCapability,
  ProductCommerceDashboardCapability
>;

export function isNavigableDashboardCapability(
  capability: DashboardCapability,
): capability is NavigableDashboardCapability {
  return DASHBOARD_SECTION_META[capability].navigable;
}

export const NAVIGABLE_DASHBOARD_CAPABILITIES = DASHBOARD_CAPABILITIES.filter(
  isNavigableDashboardCapability,
);

export const DASHBOARD_SECTION_GROUP_ORDER: DashboardSectionGroup[] = [
  "operations",
  "money",
  "content",
  "settings",
];

/**
 * The grant a manager receives when an owner first flips "Dashboard access"
 * on — the ops suite that shipped as the platform-wide hardcoded whitelist in
 * plans/manager-dashboard-access-spec.md. A starting point in the UI, never an
 * implicit fallback: an empty `managerSections` means no access, not this.
 */
export const DEFAULT_MANAGER_SECTIONS: DashboardSection[] = [
  "appointments",
  "requests",
  "report",
  "clients",
  "chat",
  "cancellations",
];

const GRANTABLE_SET: ReadonlySet<string> = new Set(
  GRANTABLE_DASHBOARD_SECTIONS,
);
const DASHBOARD_CAPABILITY_SET: ReadonlySet<string> = new Set(
  DASHBOARD_CAPABILITIES,
);

export function isDashboardSection(value: string): value is DashboardSection {
  return GRANTABLE_SET.has(value);
}

/**
 * Recognize active and planned capability vocabulary. Do not use this at a
 * live grant boundary; `isDashboardSection` is the active allowlist.
 */
export function isDashboardCapability(
  value: string,
): value is DashboardCapability {
  return DASHBOARD_CAPABILITY_SET.has(value);
}

/**
 * Drop anything that is not a live grantable key. Stored grants outlive the
 * registry — a section removed in a later release leaves dead strings in
 * `TeamMember.managerSections`, and reads must ignore them rather than throw.
 */
export function parseManagerSections(
  values: readonly string[] | null | undefined,
): DashboardSection[] {
  if (!values?.length) return [];
  const seen = new Set<DashboardSection>();
  for (const value of values) {
    if (isDashboardSection(value)) seen.add(value);
  }
  return GRANTABLE_DASHBOARD_SECTIONS.filter((section) => seen.has(section));
}

/**
 * A manager's landing page: the first section they hold, in the landing
 * priority order above. Replaces the hardcoded `/appointments` redirect — a
 * manager granted only Refunds must not be bounced to a page they cannot
 * open.
 */
export function firstGrantedSection(
  sections: readonly DashboardSection[],
): DashboardSection | null {
  const held = new Set(sections);
  return (
    GRANTABLE_DASHBOARD_SECTIONS.find(
      (section) =>
        held.has(section) && DASHBOARD_SECTION_META[section].navigable,
    ) ?? null
  );
}

/**
 * Pure declared-capability landing selector. This is not wired into live
 * dashboard redirects until Task 14 activates the planned route-bearing
 * capabilities. Keeping it separate proves that a non-navigation capability
 * such as `product-costs` can never become a landing destination by itself.
 */
export function firstDeclaredNavigableDashboardCapability(
  capabilities: readonly DashboardCapability[],
): NavigableDashboardCapability | null {
  const held = new Set(capabilities);
  return (
    NAVIGABLE_DASHBOARD_CAPABILITIES.find((capability) =>
      held.has(capability),
    ) ?? null
  );
}

/** Setup diagnostics do not grant any commerce write capability. */
export const COMMERCE_SETUP_SECTIONS = [
  "products",
  "product-inventory",
  "product-sales",
] as const satisfies readonly DashboardSection[];
